/**
 * Layers of the plan: which layer each drawing object sits on, and the ONE
 * stacking order the renderer paints and the picker reads. Pure (no DOM, no
 * state) — main.ts passes the two facts it owns: what is a room, and which room
 * a part stands in.
 *
 * - Membership lives on the objects (`layer`). Absent, or naming a layer that is
 *   gone, means the object's kind's HOME layer (`DrawingLayer.home`).
 * - A GROUP is one item: its members follow its outermost group's layer, and
 *   restack only among themselves. A group with no layer of its own takes the
 *   highest layer its members would sit on alone.
 * - A part standing in a room (ungrouped, on the room's layer or none) is
 *   NESTED: it paints right above its room and restacks among the room's other
 *   contents. Placed on another layer, it stands on its own there.
 * - Stacking inside a layer is `order` (bottom first): listed siblings paint
 *   in that order, then the unlisted ones in today's kind order (shapes, walls,
 *   floorings, rooms, parts, connections, sections, texts, arcs, leaders, clouds,
 *   tables, dimensions) and array order — so a new object lands on top, and a
 *   drawing nobody restacked paints in the order it always did, but for two
 *   things: every loose part paints over every room (Parts is above Rooms), and
 *   a room's panes paint right after what stands in it.
 * - The Imported layer (the DXF plan and the raster underlays) is pinned at the
 *   bottom and holds nothing else: it is painted on the static backdrop canvas.
 */
import { unreachable } from '../shared/exhaustive';
import type {
  Drawing,
  DrawingGroup,
  DrawingLayer,
  LayerKind,
  ProductInstance,
} from '../shared/types';
import type { DxfLayer } from '../shared/dxf';
import {
  groupById,
  groupChain,
} from './groups';
import {
  OBJECT_ARRAY,
  objectsOf,
} from './drawingObjects';

/** Default layers, bottom first — today's paint bands. */
export const LAYER_KINDS: readonly LayerKind[] = [
  'imported', 'shapes', 'walls', 'floorings', 'rooms', 'parts', 'connections', 'annotations', 'dimensions',
];

/** English name of a default layer (translated by the caller). */
export const LAYER_KIND_NAME: Record<LayerKind, string> = {
  imported: 'Imported', shapes: 'Shapes', walls: 'Walls & lines', floorings: 'Floorings', rooms: 'Rooms',
  parts: 'Parts', connections: 'Connections', annotations: 'Annotations', dimensions: 'Dimensions',
};

export const IMPORTED_LAYER = 'layer-imported';

/** What a stacked item is. `instance` covers rooms and parts. */
export type ItemKind =
  | 'arc'
  | 'cloud'
  | 'conn'
  | 'dim'
  | 'flooring'
  | 'instance'
  | 'leader'
  | 'polygon'
  | 'rcp'
  | 'section'
  | 'table'
  | 'text'
  | 'wall';

/** The schedule tables have no id of their own; these stand for them. */
export const TABLE_IDS = { scheduleTable: 'table:connections', preparationTable: 'table:preparations', flooringTable: 'table:floorings', lightingTable: 'table:lighting', pipeTable: 'table:pipes' } as const;
export type TableKey = keyof typeof TABLE_IDS;

export interface LayerItem { kind: ItemKind; id: string; obj: any; idx: number }

/** What layers.ts needs to know about rooms (main.ts owns both rules). */
export interface LayerEnv {
  isRoom(inst: ProductInstance): boolean;
  /** The room a part stands in, or null. */
  hostRoom(inst: ProductInstance): ProductInstance | null;
}

/** One row of the stacking tree: an object (a room carries its contents as
 *  children) or a group (its members and nested groups). */
export type LayerUnit =
  | { kind: 'group'; id: string; group: DrawingGroup; children: LayerUnit[] }
  | { kind: ItemKind; id: string; item: LayerItem; children: LayerUnit[] };

/** One paint call, bottom first. `roomTop` follows a room's contents: its panes
 *  and openings paint over what stands in it. */
export interface PaintItem { kind: ItemKind | 'roomTop'; id: string; obj: any; layer: string }

export interface LayerModel {
  /** Bottom first, Imported at 0; homes missing for objects that need them are
   *  supplied (`virtual`), so every object has a layer to sit on. */
  layers: DrawingLayer[];
  virtual: Set<string>;
  /** Item id or group id → layer id. */
  layerOf: Map<string, string>;
  /** Per layer: its top-level units, BOTTOM first. */
  units: Map<string, LayerUnit[]>;
  /** Every item of every visible layer except Imported, bottom first. */
  paint: PaintItem[];
  /** Item id → its index in `paint` (higher = drawn later = on top). */
  rank: Map<string, number>;
  /** Room id → the index of its `roomTop` (its panes, over what stands in it). */
  roomTop: Map<string, number>;
  /** Item id → its unit, and the unit's parent unit id (undefined = top level). */
  unitOf: Map<string, LayerUnit>;
  parentOf: Map<string, string | undefined>;
}

/** Whether the drawing has an object of this kind with this id (a schedule table:
 *  always — its three are the drawing's own). */
export function hasObject(d: Drawing, kind: ItemKind, id: string): boolean {
  const arr = objectsOf(d, kind) as Array<{ id: string }> | undefined;
  return OBJECT_ARRAY[kind] === null || (arr ?? []).some((o) => o.id === id);
}

/** Every stacked object of the drawing (Imported excluded). */
export function layerItems(d: Drawing): LayerItem[] {
  const out: LayerItem[] = [];
  // Where each kind's objects live is its adapter's (drawingObjects.ts
  // OBJECT_ARRAY, a Record over ItemKind, in this listing order); the tables have
  // no array — their objects are the drawing's schedule tables (TABLE_IDS).
  for (const kind of Object.keys(OBJECT_ARRAY) as ItemKind[]) {
    if (OBJECT_ARRAY[kind]) {
      ((objectsOf(d, kind) ?? []) as Array<{ id: string }>).forEach((obj, idx) => out.push({ kind, id: obj.id, obj, idx }));
      continue;
    }
    (Object.keys(TABLE_IDS) as TableKey[]).forEach((k, idx) => {
      const t = d[k];
      if (t) out.push({ kind: 'table', id: TABLE_IDS[k], obj: t, idx });
    });
  }
  return out;
}

/** The default layer an item's kind lives on. */
export function homeKind(item: LayerItem, env: LayerEnv): LayerKind {
  switch (item.kind) {
    case 'polygon': return 'shapes';
    case 'wall': return 'walls';
    case 'flooring': return 'floorings';
    case 'instance': return env.isRoom(item.obj) ? 'rooms' : 'parts';
    case 'conn': return 'connections';
    case 'dim': return 'dimensions';
    case 'section': case 'rcp': case 'text': case 'arc': case 'leader': case 'cloud': case 'table': return 'annotations';
    default: return unreachable(item.kind, 'item kind');
  }
}

/** Today's paint order of the kinds: what an unlisted sibling sorts by. */
function kindRank(item: LayerItem, env: LayerEnv): number {
  switch (item.kind) {
    case 'polygon': return 0;
    case 'wall': return 1;
    case 'flooring': return 2;
    case 'instance': return env.isRoom(item.obj) ? 3 : 4;
    case 'conn': return 5;
    case 'section': return 6;
    // Just over the sections: a view, as a section's drawing is.
    case 'rcp': return 6.5;
    case 'text': return 7;
    case 'arc': return 8;
    case 'leader': return 9;
    case 'cloud': return 10;
    case 'table': return 11;
    case 'dim': return 12;
  }
}

export function defaultLayers(): DrawingLayer[] {
  return LAYER_KINDS.map((k) => ({ id: `layer-${k}`, home: k }));
}

/** Its display name (English; the caller translates a default one). */
export function layerName(l: DrawingLayer, tr: (s: string) => string = (s) => s): string {
  return l.name ?? (l.home ? tr(LAYER_KIND_NAME[l.home]) : l.num != null ? `${tr('Layer')} ${l.num}` : l.id);
}

/** A drawing saved when the app named layers as text: a layer the user added
 *  and left "Layer 3" (in any of the three languages) takes that number as
 *  its default again; a default layer named its kind's word, its default.
 *  Once, on load. */
export function settleLayerNames(d: Drawing, trIn: (key: string, lang: 'en' | 'it' | 'fr') => string): void {
  const langs = ['en', 'it', 'fr'] as const;
  for (const l of d.layers ?? []) {
    const name = l.name?.trim();
    if (!name) continue;
    if (l.home) { if (langs.some((g) => trIn(LAYER_KIND_NAME[l.home!], g) === name)) delete l.name; continue; }
    if (l.num != null) continue;
    for (const g of langs) {
      const m = new RegExp(`^${trIn('Layer', g)}\\s+(\\d+)$`).exec(name);
      if (m) { l.num = Number(m[1]); delete l.name; break; }
    }
  }
}

/** The stored layers, or the defaults, with Imported forced to the bottom. */
function baseLayers(d: Drawing): DrawingLayer[] {
  const list = d.layers && d.layers.length ? [...d.layers] : defaultLayers();
  const imp = list.findIndex((l) => l.home === 'imported');
  if (imp > 0) list.unshift(list.splice(imp, 1)[0]);
  else if (imp < 0) list.unshift({ id: IMPORTED_LAYER, home: 'imported' });
  return list;
}

/** Where a missing home layer goes: just over its nearest default neighbour
 *  below (by default rank, wherever the user moved it), else just under its
 *  nearest one above, else on top. */
function insertHome(list: DrawingLayer[], kind: LayerKind): DrawingLayer {
  const want = LAYER_KINDS.indexOf(kind);
  const l: DrawingLayer = { id: `layer-${kind}`, home: kind };
  const rankAt = (i: number): number => (list[i].home ? LAYER_KINDS.indexOf(list[i].home!) : -1);
  let below = -1, above = -1;
  for (let i = 0; i < list.length; i++) {
    const r = rankAt(i);
    if (r < 0) continue;
    if (r < want && (below < 0 || r > rankAt(below))) below = i;
    if (r > want && (above < 0 || r < rankAt(above))) above = i;
  }
  const at = below >= 0 ? below + 1 : above >= 0 ? Math.max(1, above) : list.length;
  list.splice(at, 0, l);
  return l;
}

/** Resolve every object's layer and the stacking. `hidden` adds layers to skip
 *  in `paint` (a sheet's own), on top of each layer's `hidden`. */
export function layerModel(d: Drawing, env: LayerEnv, hidden?: ReadonlySet<string>): LayerModel {
  const layers = baseLayers(d);
  const virtual = new Set<string>();
  const byId = new Map(layers.map((l) => [l.id, l]));
  const items = layerItems(d);
  const itemById = new Map(items.map((it) => [it.id, it]));
  const home = (kind: LayerKind): string => {
    let l = layers.find((x) => x.home === kind);
    if (!l) { l = insertHome(layers, kind); byId.set(l.id, l); virtual.add(l.id); }
    return l.id;
  };
  const valid = (id: string | undefined): id is string => !!id && byId.has(id) && id !== layers[0].id;
  /** Where an object would sit on its own (no group, no room). */
  const own = (it: LayerItem): string => (valid(it.obj.layer) ? it.obj.layer : home(homeKind(it, env)));
  const index = (id: string): number => layers.findIndex((l) => l.id === id);

  // Groups: each outermost group takes its own layer, or its highest member's.
  const groups = d.groups || [];
  const outerOf = (gid: string | undefined): string | undefined => groupChain(d, gid).at(-1);
  const groupLayer = new Map<string, string>();
  for (const it of items) {
    const outer = outerOf(it.obj.groupId);
    if (!outer) continue;
    const g = groupById(d, outer);
    if (g && valid(g.layer)) { groupLayer.set(outer, g.layer); continue; }
    const mine = own(it);
    const was = groupLayer.get(outer);
    if (was === undefined || index(mine) > index(was)) groupLayer.set(outer, mine);
  }

  const layerOf = new Map<string, string>();
  for (const [gid, lid] of groupLayer) {
    layerOf.set(gid, lid);
    for (const g of groups) if (groupChain(d, g.id).includes(gid)) layerOf.set(g.id, lid);
  }
  // Rooms (and every non-nested object) first, then the parts nested in them.
  const nestedIn = new Map<string, string>();
  const pass2: LayerItem[] = [];
  for (const it of items) {
    const outer = outerOf(it.obj.groupId);
    if (outer) { layerOf.set(it.id, groupLayer.get(outer)!); continue; }
    if (it.kind === 'instance' && !env.isRoom(it.obj)) {
      const room = env.hostRoom(it.obj);
      if (room && room.id !== it.id && itemById.has(room.id)) { pass2.push(it); continue; }
    }
    layerOf.set(it.id, own(it));
  }
  for (const it of pass2) {
    const room = env.hostRoom(it.obj)!;
    const roomLayer = layerOf.get(room.id) ?? own(itemById.get(room.id)!);
    if (!valid(it.obj.layer) || it.obj.layer === roomLayer) {
      layerOf.set(it.id, roomLayer);
      nestedIn.set(it.id, room.id);
    } else {
      layerOf.set(it.id, it.obj.layer);
    }
  }

  // Build the units: groups hold their members, rooms their contents.
  const unitOf = new Map<string, LayerUnit>();
  const parentOf = new Map<string, string | undefined>();
  const groupUnit = (g: DrawingGroup): LayerUnit => {
    let u = unitOf.get(g.id);
    if (!u) { u = { kind: 'group', id: g.id, group: g, children: [] }; unitOf.set(g.id, u); }
    return u;
  };
  for (const it of items) unitOf.set(it.id, { kind: it.kind, id: it.id, item: it, children: [] });
  const tops = new Map<string, LayerUnit[]>(layers.map((l) => [l.id, []]));
  const attach = (u: LayerUnit, parent: string | undefined): void => {
    parentOf.set(u.id, parent);
    if (parent) unitOf.get(parent)!.children.push(u);
    else tops.get(layerOf.get(u.id)!)!.push(u);
  };
  const registered = new Set(groups.map((g) => g.id));
  for (const g of groups) if (layerOf.has(g.id)) groupUnit(g); // a group holding nothing has no row
  for (const g of groups) {
    if (!unitOf.has(g.id)) continue;
    attach(unitOf.get(g.id)!, g.parent && registered.has(g.parent) && unitOf.has(g.parent) ? g.parent : undefined);
  }
  for (const it of items) {
    const u = unitOf.get(it.id)!;
    const gid = it.obj.groupId as string | undefined;
    if (gid && registered.has(gid) && unitOf.has(gid)) attach(u, gid);
    else attach(u, nestedIn.get(it.id));
  }

  // Sort every sibling list: listed by the layer's order, then by kind / array.
  const key = (u: LayerUnit): [number, number] => {
    if (u.kind !== 'group') return [kindRank(u.item, env), u.item.idx];
    let best: [number, number] = [-1, -1];
    for (const c of u.children) {
      const k = key(c);
      if (k[0] > best[0] || (k[0] === best[0] && k[1] > best[1])) best = k;
    }
    return best;
  };
  const sortAll = (list: LayerUnit[], pos: Map<string, number>): void => {
    list.sort((a, b) => {
      const pa = pos.get(a.id), pb = pos.get(b.id);
      if (pa !== undefined && pb !== undefined) return pa - pb;
      if (pa !== undefined) return -1;
      if (pb !== undefined) return 1;
      const ka = key(a), kb = key(b);
      return ka[0] - kb[0] || ka[1] - kb[1];
    });
    for (const u of list) if (u.children.length) sortAll(u.children, pos);
  };
  for (const l of layers) {
    const pos = new Map((l.order || []).map((id, i) => [id, i]));
    sortAll(tops.get(l.id)!, pos);
  }

  // Flatten into the paint list (Imported is the backdrop's).
  const paint: PaintItem[] = [];
  const rank = new Map<string, number>();
  const roomTop = new Map<string, number>();
  const emit = (u: LayerUnit, lid: string): void => {
    if (u.kind !== 'group') {
      rank.set(u.id, paint.length);
      paint.push({ kind: u.kind, id: u.id, obj: u.item.obj, layer: lid });
    }
    for (const c of u.children) emit(c, lid);
    if (u.kind === 'instance' && env.isRoom(u.item.obj)) {
      roomTop.set(u.id, paint.length);
      paint.push({ kind: 'roomTop', id: u.id, obj: u.item.obj, layer: lid });
    }
  };
  for (const l of layers.slice(1)) {
    if (l.hidden || hidden?.has(l.id)) continue;
    for (const u of tops.get(l.id)!) emit(u, l.id);
  }
  return { layers, virtual, layerOf, units: tops, paint, rank, roomTop, unitOf, parentOf };
}

/** Is the object (or group) on a layer that is hidden (here or on this sheet)? */
export function onHiddenLayer(m: LayerModel, id: string, hidden?: ReadonlySet<string>): boolean {
  const lid = m.layerOf.get(id);
  if (!lid) return false;
  const l = m.layers.find((x) => x.id === lid);
  return !!l?.hidden || !!hidden?.has(lid);
}

export function onLockedLayer(m: LayerModel, id: string): boolean {
  const lid = m.layerOf.get(id);
  return !!lid && !!m.layers.find((x) => x.id === lid)?.locked;
}

/** Every object id a unit stands for (a group's members, a room's contents). */
export function unitObjectIds(u: LayerUnit): string[] {
  const out: string[] = [];
  const walk = (x: LayerUnit): void => { if (x.kind !== 'group') out.push(x.id); x.children.forEach(walk); };
  walk(u);
  return out;
}

/** Every object id on a layer. */
export function layerObjectIds(m: LayerModel, layerId: string): string[] {
  return (m.units.get(layerId) || []).flatMap(unitObjectIds);
}

// ---- Edits (each writes the drawing; the caller records the undo step) ----

/** Store the layers (the defaults, and any home supplied for an object) so they
 *  can be edited. */
export function materializeLayers(d: Drawing, env: LayerEnv): DrawingLayer[] {
  const m = layerModel(d, env);
  d.layers = m.layers;
  return d.layers;
}

export function addLayer(d: Drawing, env: LayerEnv, id: string, name: string | undefined, aboveId?: string): DrawingLayer {
  const list = materializeLayers(d, env);
  // Unnamed: "Layer <n>", one past the highest a layer the user added has.
  const l: DrawingLayer = name ? { id, name } : { id, num: Math.max(0, ...list.map((x) => x.num ?? 0)) + 1 };
  const at = aboveId ? list.findIndex((x) => x.id === aboveId) : -1;
  list.splice(at >= 0 ? at + 1 : list.length, 0, l);
  return l;
}

export function renameLayer(d: Drawing, env: LayerEnv, id: string, name: string): void {
  const l = materializeLayers(d, env).find((x) => x.id === id);
  if (!l) return;
  const t = name.trim();
  if (t && !(l.home && t === LAYER_KIND_NAME[l.home])) l.name = t;
  else delete l.name;
}

/** Move a layer to `index` (bottom first). Imported stays at the bottom. */
export function moveLayer(d: Drawing, env: LayerEnv, id: string, index: number): void {
  const list = materializeLayers(d, env);
  const from = list.findIndex((x) => x.id === id);
  if (from <= 0) return;
  const [l] = list.splice(from, 1);
  list.splice(Math.max(1, Math.min(list.length, index)), 0, l);
}

export function setLayerFlag(d: Drawing, env: LayerEnv, id: string, flag: 'hidden' | 'locked', on: boolean): void {
  const l = materializeLayers(d, env).find((x) => x.id === id);
  if (!l) return;
  if (on) l[flag] = true; else delete l[flag];
}

/** Remove a layer from the list (its objects are deleted by the caller first);
 *  anything still naming it falls back to its home. Imported can't go. */
export function removeLayer(d: Drawing, env: LayerEnv, id: string): void {
  const list = materializeLayers(d, env);
  const at = list.findIndex((x) => x.id === id);
  if (at <= 0) return;
  list.splice(at, 1);
  for (const it of layerItems(d)) if (it.obj.layer === id) delete it.obj.layer;
  for (const g of d.groups || []) if (g.layer === id) delete g.layer;
  for (const s of d.sheets || []) {
    if (!s.hiddenLayers) continue;
    s.hiddenLayers = s.hiddenLayers.filter((x) => x !== id);
    if (!s.hiddenLayers.length) delete s.hiddenLayers;
  }
}

/** Where a moved unit lands among its new siblings: directly over (painted
 *  after) or under one of them; neither = on top. */
export type Placement = { over?: string; under?: string };

function writeSiblingOrder(d: Drawing, layerId: string, ids: string[]): void {
  const l = (d.layers || []).find((x) => x.id === layerId);
  if (!l) return;
  const drop = new Set(ids);
  l.order = [...(l.order || []).filter((x) => !drop.has(x)), ...ids];
}

function placeAmong(siblings: string[], moved: string[], at: Placement): string[] {
  const rest = siblings.filter((x) => !moved.includes(x));
  let i = rest.length;
  if (at.over && rest.includes(at.over)) i = rest.indexOf(at.over) + 1;
  else if (at.under && rest.includes(at.under)) i = rest.indexOf(at.under);
  return [...rest.slice(0, i), ...moved, ...rest.slice(i)];
}

/** Restack units that share one parent (or the top level of one layer): they
 *  keep their relative order and land over / under `at`. */
export function restack(d: Drawing, env: LayerEnv, ids: string[], at: Placement): void {
  materializeLayers(d, env);
  const m = layerModel(d, env);
  if (!ids.length) return;
  const parent = m.parentOf.get(ids[0]);
  const lid = m.layerOf.get(ids[0])!;
  const siblings = parent ? m.unitOf.get(parent)!.children.map((u) => u.id) : (m.units.get(lid) || []).map((u) => u.id);
  const moved = siblings.filter((x) => ids.includes(x));
  writeSiblingOrder(d, lid, placeAmong(siblings, moved, at));
}

/** Bring forward / backward one step, or to the front / back, among siblings. */
export function arrange(d: Drawing, env: LayerEnv, ids: string[], how: 'front' | 'forward' | 'backward' | 'back'): boolean {
  materializeLayers(d, env);
  const m = layerModel(d, env);
  // Group by sibling list, so each set moves within its own layer / parent.
  const sets = new Map<string, { lid: string; parent?: string; moved: string[] }>();
  for (const id of ids) {
    if (!m.unitOf.has(id)) continue;
    const lid = m.layerOf.get(id)!, parent = m.parentOf.get(id);
    const k = JSON.stringify([lid, parent ?? null]);
    if (!sets.has(k)) sets.set(k, { lid, parent, moved: [] });
    sets.get(k)!.moved.push(id);
  }
  let changed = false;
  for (const { lid, parent, moved } of sets.values()) {
    const sib = (parent ? m.unitOf.get(parent)!.children : m.units.get(lid) || []).map((u) => u.id);
    let next: string[];
    if (how === 'front') next = [...sib.filter((x) => !moved.includes(x)), ...sib.filter((x) => moved.includes(x))];
    else if (how === 'back') next = [...sib.filter((x) => moved.includes(x)), ...sib.filter((x) => !moved.includes(x))];
    else {
      next = [...sib];
      const step = how === 'forward' ? 1 : -1;
      const order = how === 'forward' ? [...next].reverse() : [...next];
      for (const id of order) {
        if (!moved.includes(id)) continue;
        const i = next.indexOf(id), j = i + step;
        if (j < 0 || j >= next.length || moved.includes(next[j])) continue;
        [next[i], next[j]] = [next[j], next[i]];
      }
    }
    if (next.join() !== sib.join()) { writeSiblingOrder(d, lid, next); changed = true; }
  }
  return changed;
}

/**
 * Put units (objects or groups) on a layer, at `at` among its top-level units.
 * A group takes its members along; a room takes the contents nested in it; a
 * nested part or a group member moved alone leaves its nest. Imported takes
 * nothing, and nothing leaves it.
 */
export function moveToLayer(d: Drawing, env: LayerEnv, ids: string[], layerId: string, at: Placement = {}): void {
  materializeLayers(d, env);
  if (layerId === (d.layers || [])[0]?.id) return;
  const before = layerModel(d, env);
  const units: string[] = [];
  const setOn = (x: LayerUnit): void => {
    if (x.kind === 'group') x.group.layer = layerId;
    else x.item.obj.layer = layerId;
    x.children.forEach(setOn);
  };
  for (const id of ids) {
    // A group member (or a nested group) moved alone stands for its outermost
    // group: a group is one item.
    const u0 = before.unitOf.get(id);
    if (!u0) continue;
    const outer = groupChain(d, u0.kind === 'group' ? u0.id : u0.item.obj.groupId).at(-1);
    const u = outer && before.unitOf.has(outer) ? before.unitOf.get(outer)! : u0;
    if (units.includes(u.id)) continue;
    units.push(u.id);
    setOn(u);
  }
  // What stands inside a moved unit keeps its stacking there: the order of
  // its descendants (sorted in `before`) travels to the new layer's list.
  const inner: string[] = [];
  const walk = (x: LayerUnit): void => x.children.forEach((c) => { inner.push(c.id); walk(c); });
  for (const id of units) walk(before.unitOf.get(id)!);
  const after = layerModel(d, env);
  const siblings = (after.units.get(layerId) || []).map((u) => u.id);
  const moved = siblings.filter((x) => units.includes(x));
  writeSiblingOrder(d, layerId, [...inner, ...placeAmong(siblings, moved, at)]);
}

/** A section sent below every object: the bottom item of the bottom layer (a
 *  drawing saved before layers flagged it `below`; the section's Below objects). */
export function sendSectionBelow(d: Drawing, env: LayerEnv, id: string): void {
  const m = layerModel(d, env);
  const bottom = m.layers[1];
  if (!bottom) return;
  moveToLayer(d, env, [id], bottom.id, { under: m.units.get(bottom.id)?.find((u) => u.id !== id)?.id });
}

/** Every unit id (groups too), in stacking order, bottom first. */
export function unitStack(m: LayerModel): string[] {
  const out: string[] = [];
  const walk = (u: LayerUnit): void => { out.push(u.id); u.children.forEach(walk); };
  for (const l of m.layers.slice(1)) for (const u of m.units.get(l.id) || []) walk(u);
  return out;
}

/**
 * Copies keep the stacking their originals had INSIDE a copied group or room
 * (fresh ids are listed nowhere, so they would fall back to the kind order).
 * `stack` is the originals' unit ids in their paint order (`unitStack`),
 * `idMap` original → copy, groups included. A top-level copy, or one landing in
 * a room that was not copied, still goes on top like any new object; and
 * nothing is written when the default order already matches.
 */
export function copyInnerOrder(d: Drawing, env: LayerEnv, stack: readonly string[], idMap: ReadonlyMap<string, string>): void {
  const fresh = new Set(idMap.values());
  const m = layerModel(d, env);
  const want = new Map<string, string[]>();
  for (const id of stack) {
    const to = idMap.get(id);
    const parent = to !== undefined ? m.parentOf.get(to) : undefined;
    if (!to || !parent || !fresh.has(parent)) continue;
    (want.get(parent) ?? want.set(parent, []).get(parent)!).push(to);
  }
  const writes: Array<[string, string[]]> = [];
  for (const [parent, ids] of want) {
    const now = m.unitOf.get(parent)!.children.map((u) => u.id).filter((x) => ids.includes(x));
    if (now.join() !== ids.join()) writes.push([m.layerOf.get(parent)!, ids]);
  }
  if (!writes.length) return;
  materializeLayers(d, env);
  for (const [lid, ids] of writes) writeSiblingOrder(d, lid, ids);
}

/** Drop order entries that name nothing any more (keeps the lists short). */
export function pruneLayerOrders(d: Drawing): void {
  if (!d.layers) return;
  const live = new Set<string>([...layerItems(d).map((it) => it.id), ...(d.groups || []).map((g) => g.id)]);
  for (const l of d.layers) {
    if (!l.order) continue;
    l.order = l.order.filter((id) => live.has(id));
    if (!l.order.length) delete l.order;
  }
}

/** Every object, in stacking order (bottom first), hidden layers INCLUDED —
 *  what an export writes in paint order (the paint list's walk, which leaves the
 *  hidden layers out). Imported is not in it. */
export function stackOrder(m: LayerModel): LayerItem[] {
  const out: LayerItem[] = [];
  const walk = (u: LayerUnit): void => { if (u.kind !== 'group') out.push(u.item); u.children.forEach(walk); };
  for (const l of m.layers.slice(1)) for (const u of m.units.get(l.id) || []) walk(u);
  return out;
}

/**
 * DXF layer names for the layers: AutoCAD R12 takes up to 31 characters from
 * A–Z 0–9 $ _ -, so a name is upper-cased, anything else becomes `_`, it is cut
 * to `max` characters (room for a `-BENCHES` style suffix) and a clash gets
 * `_2`, `_3`… A clash counts the sub-layers too (`<name>-BENCHES` …, `suffixes`)
 * and DXF's own layer `0`, whose flags the writer owns: a layer whose name is
 * ANOTHER layer's sub-layer (`Annotations-Cut` beside Annotations) is the one
 * renamed, wherever it sits — so a default layer keeps its name. A default layer
 * is written by its ENGLISH name (a CAD standard does not change with the UI
 * language); a user's name as typed.
 */
export const DXF_SUB_LAYERS = ['BENCHES', 'GLASS', 'DOORS', 'CEILING', 'LIGHTS', 'CUT', 'ELEV', 'SGLASS', 'MARK', 'GRILLE'] as const;
export type DxfSubLayer = (typeof DXF_SUB_LAYERS)[number];

export function dxfLayerNames(layers: DrawingLayer[], max = 23, suffixes: readonly string[] = DXF_SUB_LAYERS): Map<string, string> {
  const subs = (n: string): string[] => suffixes.map((s) => `${n}-${s}`);
  const raw = layers.map((l) => {
    const name = l.name ?? (l.home ? LAYER_KIND_NAME[l.home] : l.num != null ? `Layer ${l.num}` : l.id);
    return name.toUpperCase().replace(/[^A-Z0-9$_-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, max) || 'LAYER';
  });
  const used = new Set<string>(['0']), taken = new Set<string>();
  const out = new Map<string, string>();
  layers.forEach((l, i) => {
    // The layers above keep their names unless they clash, so their sub-layers
    // are spoken for already.
    const ahead = new Set(raw.slice(i + 1).flatMap(subs));
    const base = raw[i];
    let name = base;
    for (let k = 2; used.has(name) || taken.has(name) || ahead.has(name) || subs(name).some((n) => used.has(n)); k++) name = `${base.slice(0, max - String(k).length - 1)}_${k}`;
    used.add(name);
    for (const n of subs(name)) taken.add(n);
    out.set(l.id, name);
  });
  return out;
}

/** The sub-layers declared only under a layer that received one of their
 *  entities — a room's ceiling lines, its lights (dashed, overhead, in the
 *  lights' amber: LIGHT_ACI) and a section's four kinds — with their look, in
 *  the order they follow the layer's own entries. */
export const DXF_USED_SUB_LAYERS: ReadonlyArray<Omit<DxfLayer, 'name' | 'off' | 'locked'> & { sub: DxfSubLayer }> = [
  { sub: 'CEILING', color: 8, ltype: 'DASHED' },
  { sub: 'LIGHTS', color: 40, ltype: 'DASHED' },
  { sub: 'CUT', color: 7 }, { sub: 'ELEV', color: 8 },
  { sub: 'SGLASS', color: 4 }, { sub: 'MARK', color: 1 },
  { sub: 'GRILLE', color: 42 },
];

/** The export's layer table plus `<base>-<sub>` for each sub-layer an entity
 *  landed on under `base` (`used`: base → its subs), off / locked with its
 *  layer, right after the base's other entries — so a drawing that writes none
 *  of them declares the table it always did. */
export function withUsedSubLayers(table: readonly DxfLayer[], used: ReadonlyMap<string, ReadonlySet<string>>): DxfLayer[] {
  const out = table.slice();
  for (const [base, subs] of used) {
    const own = out.find((l) => l.name === base);
    const family = new Set([base, ...DXF_SUB_LAYERS.map((s) => `${base}-${s}`)]);
    for (const spec of DXF_USED_SUB_LAYERS) {
      if (!subs.has(spec.sub)) continue;
      let after = -1;
      out.forEach((l, i) => { if (family.has(l.name)) after = i; });
      const { sub, ...look } = spec;
      const entry: DxfLayer = { name: `${base}-${sub}`, ...look, off: !!own?.off, locked: !!own?.locked };
      if (after < 0) out.push(entry); else out.splice(after + 1, 0, entry);
    }
  }
  return out;
}
